import React, { useEffect } from 'react';
import { useLocation, useNavigate } from "react-router";
import { useIntl } from "react-intl";
import { UTCToLocalString } from "@/util/dateUtil";
import { clickFromKeyboard } from '@/util/keyboardUtil';
import { usePaginatedAgents } from "@/query/agent";
import { useModal, usePageParam, useRouteHandler } from "@/hook";
import { SearchBar } from "@/component/input";
import AppMenu from '@/component/AppMenu';
import Button from "@/component/Button";
import AccountPopover from "@/component/AccountPopover";
import PaginationBar from "@/component/PaginationBar";
import HighlightText from '@/component/HighlightText';
import { AddIcon } from "@/assets/svgs/icons";

import AgentCreateModal, { AGENT_CREATE_MODAL_ID } from "./component/AgentCreateModal";


const AGENT_PAGE_SIZE = 15;
interface AgentListViewPageParam {
    page: number;
    size: number;
    searchKeyword?: string;
}

const AgentListView: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const location = useLocation();
    const navigate = useNavigate();
    const { moveToDashboard } = useRouteHandler();

    const { param, set: setParam } = usePageParam<AgentListViewPageParam>({
        page: 0,
        size: AGENT_PAGE_SIZE,
        searchKeyword: undefined,
    });

    const { data: agentPage, refetch, isRefetching } = usePaginatedAgents(
        param?.page,
        param?.size,
        true,
        param?.searchKeyword,
    );

    useEffect(() => {
        if (!location.state?.needRefetch) {
            return;
        }
        refetch();
        navigate(".", { replace: true, state: {} });
    }, [location.state?.needRefetch, refetch, navigate]);

    if (isRefetching) {
        return null;
    }
    return (<>
        <AgentCreateModal />

        <div className="size-full flex flex-col bg-black-100">
            <div className="h-[72px] px-3 py-[15px] flex justify-between bg-chat42">
                <div className="flex items-center gap-2 rounded-[6px]">
                    <AppMenu className="text-white hover:text-chat42 hover:bg-chat42-20" />
                    <div className="p-1 flex flex-col gap-0.5 cursor-default">
                        <div className="text-white text-[14px] font-[600] leading-none">
                            Chat42
                        </div>
                        <div className="text-white text-[17px] font-[700] leading-none">
                            NLU
                        </div>
                    </div>
                </div>
                <AccountPopover anchor="bottom end"
                                className="mt-2"
                />
            </div>
            <div className="w-full flex-1 p-5 flex min-[832px]:justify-center overflow-y-hidden overflow-x-auto">
                <div className="w-md-page flex flex-col items-center">
                    <SearchBar lg
                               placeholder={intl.formatMessage({ id: "i01011" })}
                               onSearch={keyword => setParam({ searchKeyword: keyword })}
                    />

                    <div className="w-full mt-3 flex items-center justify-between">
                        <div className="ml-0.5">
                            {`${intl.formatMessage({ id: "i01019" })} (${agentPage?.total ?? 0})`}
                        </div>

                        <Button variant="text"
                                className="my-1"
                                onClick={() => modal.open(AGENT_CREATE_MODAL_ID)}
                        >
                            <AddIcon className="size-4" />
                            {intl.formatMessage({ id: "i01001" })}
                        </Button>
                    </div>

                    <div role="table"
                         className="selection-table grid-table-agent mt-1"
                    >
                        <div role="rowgroup"
                             className="selection-table-thead"
                        >
                            <div role="columnheader"
                                 className="selection-table-th pl-[25px]"
                            >
                                {intl.formatMessage({ id: "i01002" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "i01015" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "i01020" })}
                            </div>
                            <div role="columnheader"
                                 className="selection-table-th pl-[15px]"
                            >
                                {intl.formatMessage({ id: "i01021" })}
                            </div>
                        </div>
                        <div role="rowgroup"
                             className="selection-table-tbody"
                        >
                            {agentPage?.content.map(agent => (
                                <div key={agent.id}
                                     tabIndex={0}
                                     role="row"
                                     className="selection-table-tr grid-table-agent hover:bg-chat42-5 cursor-pointer"
                                     onClick={() => moveToDashboard(agent.id)}
                                     onKeyDown={clickFromKeyboard}
                                >
                                    <div role="cell"
                                         className="selection-table-td pl-[25px]"
                                    >
                                        <HighlightText query={agent.name}
                                                       keyword={param?.searchKeyword}
                                                       highlightClassName="text-highlight"
                                        />
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        <HighlightText query={`${agent.owner?.nickname}(${agent.owner?.email})`}
                                                       keyword={param?.searchKeyword}
                                                       highlightClassName="text-highlight"
                                        />
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        {UTCToLocalString(agent.updated_at, 'YYYY-MM-DD HH:mm')}
                                    </div>
                                    <div role="cell"
                                         className="selection-table-td pl-[15px]"
                                    >
                                        {UTCToLocalString(agent.created_at, 'YYYY-MM-DD HH:mm')}
                                    </div>
                                </div>
                            ))}
                            {agentPage?.content.length === 0 && (
                                <div className="empty-table">
                                    {intl.formatMessage({ id: param?.searchKeyword ? "b0014" : "b0018" })}
                                </div>
                            )}
                        </div>
                    </div>

                    <PaginationBar className="mt-2"
                                   pageData={agentPage}
                                   onClickPage={page => setParam({ page })}
                    />
                </div>
            </div>
        </div>
    </>);
};

export default AgentListView;
